// AccesoStreamingSection.js — Páginas «Iniciar sesión» y «Crear cuenta» (tipo
// `acceso_streaming`) con el lenguaje del catálogo: muro de portadas de fondo, a
// la izquierda un mensaje con lo que ofrece la plataforma y a la derecha el
// panel de acceso en una tarjeta de vidrio. El formulario es el mismo del modal
// (AccesoIconoModal en modo `enPagina`), así que la lógica no se duplica.
// `?vista=olvido` abre directo la recuperación; `?volver=/ruta` decide a dónde ir
// después de entrar.
import React, { useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { BACKEND_BASE } from '../../../services/api';
import MuroCursos from './MuroCursos';
import AccesoIconoModal from '../AccesoIconoModal';
import RegistroStreaming from '../RegistroStreaming';

const FUENTE = "'Bricolage Grotesque', 'Inter', system-ui, sans-serif";

export const ACCESO_DEF = {
  entrar: {
    eyebrow: 'Bienvenido de nuevo',
    titulo: 'Tu próxima clase te está esperando',
    texto: 'Entra y retoma el curso en el minuto exacto donde lo dejaste.',
  },
  registro: {
    eyebrow: 'Crea tu cuenta',
    titulo: 'Aprende a tu ritmo, con certificado',
    texto: 'Cursos en video para profesionales de la salud, hechos por especialistas.',
  },
  beneficios: [
    { icono: 'video', texto: 'Clases en video que retomas donde quedaste' },
    { icono: 'certificado', texto: 'Certificado al completar cada curso' },
    { icono: 'dispositivos', texto: 'En el computador, la tableta o el celular' },
  ],
};

const P = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.9, strokeLinecap: 'round', strokeLinejoin: 'round' };
export const ICONOS_ACCESO = {
  video: <svg viewBox="0 0 24 24" {...P}><rect x="3" y="5" width="18" height="14" rx="3" /><path d="m10.5 9 4.5 3-4.5 3Z" /></svg>,
  certificado: <svg viewBox="0 0 24 24" {...P}><circle cx="12" cy="9" r="5" /><path d="m9.5 13.3-1.5 7.2 4-2.2 4 2.2-1.5-7.2" /></svg>,
  dispositivos: <svg viewBox="0 0 24 24" {...P}><rect x="2.5" y="5" width="14" height="10" rx="1.5" /><path d="M6 19h7" /><rect x="17" y="9" width="4.5" height="10" rx="1.2" /></svg>,
  aval: <svg viewBox="0 0 24 24" {...P}><path d="M12 3 5 6v5c0 4.4 3 8.3 7 9.5 4-1.2 7-5.1 7-9.5V6Z" /><path d="m9 12 2.2 2.2L15.5 10" /></svg>,
  reloj: <svg viewBox="0 0 24 24" {...P}><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" /></svg>,
  especialistas: <svg viewBox="0 0 24 24" {...P}><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20c.8-3.4 3.4-5.5 6.5-5.5s5.7 2.1 6.5 5.5" /><path d="M16 4.6a3.5 3.5 0 0 1 0 6.8M18 14.8c1.8.7 3 2.5 3.5 5.2" /></svg>,
};

const entrar = keyframes`from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: none; }`;

const Raiz = styled.div`
  --acento: ${p => p.$acento};
  position: relative; isolation: isolate; overflow: hidden; min-height: 100vh; min-height: 100dvh;
  display: grid; grid-template-columns: minmax(0, 1.15fr) ${p => (p.$registro ? 'minmax(360px, 580px)' : 'minmax(360px, 460px)')}; gap: clamp(2rem, 6vw, 6rem);
  align-items: ${p => (p.$registro ? 'start' : 'center')};
  > .mensaje { ${p => (p.$registro ? 'position: sticky; top: 9rem;' : '')} }
  @media (max-width: 900px) { > .mensaje { position: static; } }
  padding: clamp(7rem, 11vw, 8.5rem) 4vw clamp(3rem, 6vw, 5rem);
  background: ${p => p.$fondo}; color: #d5d9df; font-family: ${FUENTE};
  @media (max-width: 900px) { grid-template-columns: 1fr; gap: 2rem; align-items: start; padding-top: 6.5rem; }
`;
const Mensaje = styled.div`
  max-width: 40rem; animation: ${entrar} .6s cubic-bezier(.2,.8,.2,1) both;
  .eyebrow { display: inline-flex; align-items: center; gap: .6rem; font-size: .95rem; font-weight: 600; color: #e7eaee; }
  .eyebrow::before { content: ''; width: 28px; height: 2px; background: var(--acento); }
  h1 { margin: 1rem 0 0; font-size: clamp(2.6rem, 6.2vw, 5.4rem); font-weight: 800; font-stretch: 76%; line-height: .92; letter-spacing: -.02em; color: #fff; text-wrap: balance; }
  > p { margin: 1.3rem 0 0; max-width: 32rem; font-size: 1.12rem; line-height: 1.6; color: #c3c9d1; }
  ul { list-style: none; margin: 2.2rem 0 0; padding: 0; display: grid; gap: .9rem; }
  li { display: flex; align-items: center; gap: .9rem; font-size: 1rem; color: #e7eaee; }
  li i { display: grid; place-items: center; flex: none; width: 42px; height: 42px; border-radius: 12px;
    background: rgba(255,255,255,.07); box-shadow: inset 0 0 0 1px rgba(255,255,255,.1); color: var(--acento);
    backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px); }
  li svg { width: 21px; height: 21px; }
  .cifra { display: inline-flex; align-items: center; gap: .9rem; margin-top: 2.2rem; padding: .45rem 1.1rem .45rem .45rem; border-radius: 999px;
    background: rgba(0,0,0,.4); box-shadow: inset 0 0 0 1px rgba(255,255,255,.1); font-size: .92rem; color: #c3c9d1;
    backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); text-decoration: none; transition: box-shadow .2s, background .2s; }
  a.cifra:hover { background: rgba(0,0,0,.55); box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--acento) 60%, transparent); }
  a.cifra:focus-visible { outline: 2px solid var(--acento); outline-offset: 3px; }
  .cifra b { font-size: 1.1rem; font-weight: 800; color: #fff; }
  .pila { display: flex; }
  .pila img { width: 52px; height: 34px; object-fit: cover; border-radius: 7px; box-shadow: 0 0 0 2px ${p => p.$fondo}; transition: transform .25s; }
  .pila img + img { margin-left: -16px; }
  a.cifra:hover .pila img:nth-child(1) { transform: translateX(-3px) rotate(-4deg); }
  a.cifra:hover .pila img:nth-child(3) { transform: translateX(3px) rotate(4deg); }
  .cifra .flecha { width: 16px; height: 16px; color: var(--acento); transition: transform .2s; }
  a.cifra:hover .flecha { transform: translateX(3px); }
  @media (max-width: 900px) {
    h1 { font-size: clamp(2.3rem, 10vw, 3.2rem); }
    > p { font-size: 1rem; }
    ul, .cifra { display: none; }
  }
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;
const Tarjeta = styled.div`
  position: relative; padding: clamp(1.6rem, 3vw, 2.4rem); border-radius: 24px;
  background: linear-gradient(160deg, rgba(30,30,30,.82), rgba(16,16,16,.88));
  box-shadow: inset 0 0 0 1px rgba(255,255,255,.1), 0 40px 100px -40px rgba(0,0,0,.95);
  backdrop-filter: blur(18px) saturate(130%); -webkit-backdrop-filter: blur(18px) saturate(130%);
  animation: ${entrar} .6s .12s cubic-bezier(.2,.8,.2,1) both;
  &::before { content: ''; position: absolute; inset: 0 20% auto; height: 1px; background: linear-gradient(90deg, transparent, var(--acento), transparent); opacity: .8; }
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;
const Beneficios = styled.ul`
  display: none;
  @media (max-width: 900px) {
    display: grid; gap: .7rem; list-style: none; margin: 0; padding: 0;
    li { display: flex; align-items: center; gap: .8rem; font-size: .95rem; color: #c3c9d1; }
    i { display: grid; place-items: center; flex: none; width: 36px; height: 36px; border-radius: 10px; background: rgba(255,255,255,.06); color: var(--acento); }
    svg { width: 19px; height: 19px; }
  }
`;
const Ayuda = styled.span`
  color: #8a929d;
  a { color: #e7eaee; font-weight: 600; text-decoration: underline; text-decoration-color: var(--acento); text-underline-offset: 3px; }
`;
const Legal = styled.span`
  a { color: #e7eaee; text-decoration: underline; text-decoration-color: var(--acento); text-underline-offset: 3px; }
`;

const AccesoStreamingSection = ({ config = {}, proyecto }) => {
  const location = useLocation();
  const q = new URLSearchParams(location.search);
  const modo = location.pathname.startsWith('/register') || config.modo === 'registro' ? 'registro' : 'entrar';
  const vistaInicial = q.get('vista') === 'olvido' ? 'olvido' : modo;
  const cat = (proyecto?.config_json?.landing_secciones || []).find(s => s.tipo === 'catalogo_streaming')?.config || {};
  const header = (proyecto?.config_json?.landing_secciones || []).find(s => s.tipo === 'header')?.config || {};
  const acento = config.color_acento || cat.color_acento || '#E50914';
  const fondo = config.color_fondo || cat.color_fondo || '#141414';
  const t = { ...ACCESO_DEF[modo], ...Object.fromEntries(Object.entries(config[modo] || {}).filter(([, v]) => v)) };
  const beneficios = (Array.isArray(config.beneficios) && config.beneficios.length ? config.beneficios : ACCESO_DEF.beneficios).filter(b => b.texto);
  const totalCursos = useMemo(() => (cat.items || []).length, [cat.items]);
  // Tres portadas para la pila junto a la cifra.
  const portadas = useMemo(() => {
    const docs = proyecto?.documentos || [];
    const url = (v) => (!v ? '' : (/^https?:\/\//.test(v) || v.startsWith('/') ? v : `${BACKEND_BASE}/${v}`));
    return [...new Set((cat.items || []).map(it => url(it.imagen || it.imagen_cartelera || docs.find(d => String(d.id) === String(it.documento_id))?.imagen)).filter(Boolean))].slice(0, 3);
  }, [cat.items, proyecto]);
  // Solo rutas internas en ?volver= (nada de mandar a otro sitio después de entrar).
  const volver = q.get('volver');
  const destino = volver && /^\/(?!\/)/.test(volver) ? volver : (proyecto?.config_json?.auth_config?.destino_con_sesion || header.login_destino || '/');

  const legal = config.mostrar_legal === false ? null : (
    <Legal>Al crear tu cuenta aceptas los <Link to="/terminos-de-uso">Términos de uso</Link> y la <Link to="/politica-de-privacidad">Política de privacidad</Link>.</Legal>
  );

  return (
    <Raiz $acento={acento} $fondo={fondo} $registro={modo === 'registro' && vistaInicial !== 'olvido'}>
      <MuroCursos proyecto={proyecto} fondo={fondo} acento={acento} imagen={config.imagen_banner} opacidad={0.6} />
      <Mensaje className="mensaje">
        {t.eyebrow && <span className="eyebrow">{t.eyebrow}</span>}
        <h1>{t.titulo}</h1>
        {t.texto && <p>{t.texto}</p>}
        {beneficios.length > 0 && (
          <ul>{beneficios.map((b, i) => <li key={i}><i aria-hidden="true">{ICONOS_ACCESO[b.icono] || ICONOS_ACCESO.video}</i>{b.texto}</li>)}</ul>
        )}
        {config.mostrar_cifra !== false && totalCursos > 0 && (
          <Link className="cifra" to="/">
            {portadas.length > 0 && <span className="pila" aria-hidden="true">{portadas.map(src => <img key={src} src={src} alt="" />)}</span>}
            <span><b>{totalCursos}</b> cursos disponibles</span>
            <svg className="flecha" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 5 7 7-7 7" /></svg>
          </Link>
        )}
      </Mensaje>
      <Tarjeta>
        {modo === 'registro' && vistaInicial !== 'olvido' && config.registro_completo !== false ? (
          <RegistroStreaming config={{ ...(config.textos || {}), listas: config.listas }} destino={destino} />
        ) : (
        <AccesoIconoModal
          key={vistaInicial === "olvido" ? "olvido" : "acceso"}
          enPagina
          vistaInicial={vistaInicial}
          acento={acento}
          textoAcento={header.color_texto_boton_registro || '#fff'}
          fondo={fondo}
          fuente={FUENTE}
          destino={destino}
          textos={{ ...(config.textos || {}), legal_registro: legal,
            ayuda_entrar: config.mostrar_ayuda === false ? null : <Ayuda>{(config.textos || {}).ayuda_texto || ((config.textos || {}).olvido_destacado === false ? '¿Problemas para entrar?' : '¿Otro problema?')} <Link to="/contactanos?tema=cuenta">{(config.textos || {}).ayuda_enlace || 'Escríbenos'}</Link></Ayuda>, ocultar_registro: config.crear_cuenta_en_login !== true }}
        />
        )}
      </Tarjeta>
      {beneficios.length > 0 && (
        <Beneficios>{beneficios.map((b, i) => <li key={i}><i aria-hidden="true">{ICONOS_ACCESO[b.icono] || ICONOS_ACCESO.video}</i>{b.texto}</li>)}</Beneficios>
      )}
    </Raiz>
  );
};

export default AccesoStreamingSection;
